import { useState, useEffect } from "react";
import {
  Box, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton,
  Grid, Autocomplete, Select, MenuItem, FormControl, InputLabel,
  Card, CardContent, Menu, MenuItem as MenuItemMui,
  Collapse, Pagination, Chip, Divider, useMediaQuery, useTheme,
  Drawer, Stack
} from "@mui/material";
import {
  Add, Delete, Search, MoreVert,
  PictureAsPdf, Visibility, FilterList, Close, Store,
  Business, FilterAlt, ShoppingCart, KeyboardArrowDown
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { fournisseurService } from 'services/fournisseurService';
import { produitService } from 'services/produitService';
import api from '../../services/auth';
import pdfService from 'services/pdfService';

const AchatsFournisseurs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [achats, setAchats] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [succursales, setSuccursales] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [selectedSuccursale, setSelectedSuccursale] = useState(null);
  const [dateAchat, setDateAchat] = useState("");
  const [notes, setNotes] = useState("");
  const [lignes, setLignes] = useState([{
    produit: null, produitNom: "", conditionnement: "",
    quantite: "", quantite_par_conditionnement: "",
    prix_achat_unitaire: "", prix_vente_unitaire: ""
  }]);
  const [totalAchat, setTotalAchat] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAchat, setSelectedAchat] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [fournisseurFilter, setFournisseurFilter] = useState(null);
  const [succursaleFilter, setSuccursaleFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAchats, setTotalAchats] = useState(0);
  const [pageSize] = useState(10);

  const conditionnementOptions = [
    { value: "unite", label: "Unité" },
    { value: "caisse", label: "Caisse" },
    { value: "carton", label: "Carton" }
  ];

  const formatMontant = (montantString) => {
    const montant = parseFloat(montantString) || 0;
    return montant.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try { return new Date(dateString).toLocaleDateString('fr-FR'); }
    catch { return dateString; }
  };

  const calculateLigneTotal = (ligne) => {
    const qte = parseFloat(ligne.quantite) || 0;
    const qteParCond = parseFloat(ligne.quantite_par_conditionnement) || 0;
    const prix = parseFloat(ligne.prix_achat_unitaire) || 0;
    return (qte * qteParCond * prix).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  useEffect(() => {
    setDateAchat(new Date().toISOString().split('T')[0]);
  }, []);

  const fetchFournisseurs = async () => {
    try {
      const response = await fournisseurService.getAllFournisseurs();
      setFournisseurs(response.results || response.data || response);
    } catch { toast.error("Erreur lors du chargement des fournisseurs"); }
  };

  const fetchProduits = async () => {
    try {
      const data = await produitService.getAllProduits();
      setProduits(data.results || data);
    } catch { toast.error("Erreur lors du chargement des produits"); }
  };

  const fetchSuccursales = async () => {
    try {
      setLoading(true);
      const response = await api.get('/succursales/');
      let succursalesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) succursalesData = response.data;
        else if (response.data.results) succursalesData = response.data.results;
        else if (response.data.data) succursalesData = response.data.data;
      }
      setSuccursales(succursalesData);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.succursale_principale_id) {
        const sp = succursalesData.find(s => s.id === user.succursale_principale_id);
        if (sp) setSelectedSuccursale(sp);
      } else if (succursalesData.length === 1) {
        setSelectedSuccursale(succursalesData[0]);
      }
    } catch { toast.error("Erreur lors du chargement des succursales"); }
    finally { setLoading(false); }
  };

  const fetchAchats = async (pageNum = page, filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', pageNum);
      params.append('page_size', pageSize);
      if (filters.searchTerm?.trim()) params.append('search', filters.searchTerm.trim());
      if (filters.dateDebut?.trim()) params.append('start_date', filters.dateDebut.trim());
      if (filters.dateFin?.trim()) params.append('end_date', filters.dateFin.trim());
      if (activeFilterType === 'fournisseur' && filters.fournisseurId) params.append('fournisseur', filters.fournisseurId);
      else if (activeFilterType === 'succursale' && filters.succursaleId) params.append('succursale', filters.succursaleId);
      else {
        if (filters.fournisseurId) params.append('fournisseur', filters.fournisseurId);
        if (filters.succursaleId) params.append('succursale', filters.succursaleId);
      }
      const url = `https://api.sgpme.biz/api/depot/achats/?${params.toString()}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      let achatsData = [], totalCount = 0;
      if (Array.isArray(data)) { achatsData = data; totalCount = data.length; setTotalPages(1); }
      else if (data.results) { achatsData = data.results; totalCount = data.count || 0; setTotalPages(Math.ceil(totalCount / pageSize) || 1); }
      else { achatsData = data; totalCount = data.length || 0; setTotalPages(1); }
      setAchats(achatsData);
      setTotalAchats(totalCount);
    } catch { toast.error("Erreur lors du chargement des achats"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFournisseurs(); fetchProduits(); fetchSuccursales(); fetchAchats(); }, []);

  useEffect(() => {
    const filters = { searchTerm, dateDebut, dateFin, fournisseurId: fournisseurFilter?.id, succursaleId: succursaleFilter?.id };
    setPage(1);
    const timer = setTimeout(() => fetchAchats(1, filters), 500);
    return () => clearTimeout(timer);
  }, [searchTerm, dateDebut, dateFin, fournisseurFilter, succursaleFilter, activeFilterType]);

  useEffect(() => {
    const filters = { searchTerm, dateDebut, dateFin, fournisseurId: fournisseurFilter?.id, succursaleId: succursaleFilter?.id };
    fetchAchats(page, filters);
  }, [page]);

  useEffect(() => {
    const total = lignes.reduce((sum, ligne) => {
      return sum + ((parseFloat(ligne.quantite) || 0) * (parseFloat(ligne.quantite_par_conditionnement) || 0) * (parseFloat(ligne.prix_achat_unitaire) || 0));
    }, 0);
    setTotalAchat(total);
  }, [lignes]);

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...lignes];
    newLignes[index][field] = value;
    if (field === 'produitNom' && value) {
      const found = produits.find(p => p.nom?.toLowerCase() === value.toLowerCase().trim());
      if (found) {
        newLignes[index].produit = found.id;
        newLignes[index].conditionnement = found.conditionnement || "";
        newLignes[index].quantite_par_conditionnement = found.quantite_par_conditionnement || "";
      } else { newLignes[index].produit = null; }
    }
    setLignes(newLignes);
  };

  const handleAddLigne = () => setLignes([...lignes, { produit: null, produitNom: "", conditionnement: "", quantite: "", quantite_par_conditionnement: "", prix_achat_unitaire: "", prix_vente_unitaire: "" }]);
  const handleRemoveLigne = (index) => { if (lignes.length > 1) { const n = [...lignes]; n.splice(index, 1); setLignes(n); } };
  const handleMenuOpen = (event, achat) => { setAnchorEl(event.currentTarget); setSelectedAchat(achat); };
  const handleMenuClose = () => setAnchorEl(null);
  const handleViewDetails = () => { setDetailDialogOpen(true); handleMenuClose(); };

  const downloadAchatPDF = async (achat) => {
    try {
      if (!achat?.id) { toast.error("Achat invalide"); return; }
      setLoading(true);
      let achatComplet = achat;
      if (!achat.lignes?.length || !achat.fournisseur_detail) {
        const response = await api.get(`/depot/achats/${achat.id}/`);
        achatComplet = response.data;
      }
      await pdfService.generateAchatPDF(achatComplet);
      toast.success("PDF généré avec succès !");
    } catch { toast.error("Erreur lors de la génération du PDF"); }
    finally { setLoading(false); }
  };

  const downloadRapportPDF = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page_size', '1000');
      if (dateDebut) params.append('start_date', dateDebut);
      if (dateFin) params.append('end_date', dateFin);
      if (searchTerm) params.append('search', searchTerm);
      if (activeFilterType === 'fournisseur' && fournisseurFilter) params.append('fournisseur', fournisseurFilter.id);
      else if (activeFilterType === 'succursale' && succursaleFilter) params.append('succursale', succursaleFilter.id);
      else {
        if (fournisseurFilter) params.append('fournisseur', fournisseurFilter.id);
        if (succursaleFilter) params.append('succursale', succursaleFilter.id);
      }
      const url = `https://api.sgpme.biz/api/depot/achats/?${params.toString()}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` } });
      if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
      const data = await response.json();
      let achatsData = Array.isArray(data) ? data : data.results || data;
      if (!achatsData.length) { toast.warning("Aucun achat à exporter avec ces filtres"); return; }
      const montantTotal = achatsData.reduce((sum, a) => sum + parseFloat(a.montant_total || 0), 0);
      await pdfService.generateRapportPDF(achatsData, { dateDebut, dateFin, searchTerm, fournisseurNom: fournisseurFilter?.nom, succursaleNom: succursaleFilter?.nom, typeFiltre: activeFilterType }, { totalAchats: achatsData.length, montantTotal, moyenneAchat: montantTotal / (achatsData.length || 1), totalProduits: achatsData.reduce((s, a) => s + (a.lignes?.length || 0), 0), nbFournisseurs: new Set(achatsData.map(a => a.fournisseur)).size });
      toast.success("Rapport PDF généré avec succès !");
    } catch { toast.error("Erreur lors de la génération du rapport PDF"); }
    finally { setLoading(false); }
  };

  const resetFilters = () => { setDateDebut(""); setDateFin(""); setFournisseurFilter(null); setSuccursaleFilter(null); setSearchTerm(""); setActiveFilterType('all'); setPage(1); };

  const handleCreateQuickProduit = async (index) => {
    const ligne = lignes[index];
    if (!ligne.produitNom.trim()) { toast.error("Veuillez d'abord saisir un nom de produit"); return; }
    if (!ligne.conditionnement) { toast.error("Veuillez sélectionner un conditionnement"); return; }
    try {
      const produitCree = await produitService.createProduit({ nom: ligne.produitNom, conditionnement: ligne.conditionnement, quantite_par_conditionnement: parseFloat(ligne.quantite_par_conditionnement) || 1, prix_achat: parseFloat(ligne.prix_achat_unitaire) || 0, prix_vente: parseFloat(ligne.prix_vente_unitaire) || 0, stock_initial: 0, seuil_alerte: 10, est_actif: true });
      const newLignes = [...lignes];
      newLignes[index].produit = produitCree.id;
      setLignes(newLignes);
      toast.success(`Produit "${ligne.produitNom}" créé avec succès !`);
      fetchProduits();
    } catch { toast.error("Erreur lors de la création du produit"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFournisseur) { toast.error("Veuillez sélectionner un fournisseur"); return; }
    if (!selectedSuccursale) { toast.error("Veuillez sélectionner une succursale"); return; }
    if (!dateAchat) { toast.error("Veuillez sélectionner une date"); return; }
    const hasInvalidLines = lignes.some(l => !l.produitNom.trim() || !l.conditionnement || !l.quantite || parseFloat(l.quantite) <= 0 || !l.quantite_par_conditionnement || parseFloat(l.quantite_par_conditionnement) <= 0 || !l.prix_achat_unitaire || parseFloat(l.prix_achat_unitaire) < 0 || !l.prix_vente_unitaire || parseFloat(l.prix_vente_unitaire) < 0);
    if (hasInvalidLines) { toast.error("Veuillez remplir correctement tous les champs des produits"); return; }
    try {
      const lignesAvecProduits = await Promise.all(lignes.map(async (ligne) => {
        let produitId = ligne.produit;
        let produitExistant = false;
        if (!produitId) {
          try {
            const produitCree = await produitService.createProduit({ nom: ligne.produitNom, conditionnement: ligne.conditionnement, quantite_par_conditionnement: parseFloat(ligne.quantite_par_conditionnement), prix_achat: parseFloat(ligne.prix_achat_unitaire), prix_vente: parseFloat(ligne.prix_vente_unitaire), stock_initial: 0, seuil_alerte: 10, est_actif: true });
            produitId = produitCree.id;
            toast.success(`Produit "${ligne.produitNom}" créé`);
          } catch { throw new Error(`Impossible de créer le produit "${ligne.produitNom}"`); }
        } else {
          produitExistant = true;
        }
        if (produitExistant && produitId) {
          try { await produitService.updateProduit(produitId, { prix_achat: parseFloat(ligne.prix_achat_unitaire), prix_vente: parseFloat(ligne.prix_vente_unitaire) }); }
          catch (error) { console.warn(`Impossible de mettre à jour le produit ${produitId}:`, error); }
        }
        return { produit: Number(produitId), quantite: parseFloat(ligne.quantite), quantite_par_conditionnement: parseFloat(ligne.quantite_par_conditionnement), prix_achat_unitaire: parseFloat(ligne.prix_achat_unitaire), prix_vente_unitaire: parseFloat(ligne.prix_vente_unitaire) };
      }));
      if (lignesAvecProduits.some(l => isNaN(l.produit) || l.produit <= 0)) throw new Error("Certains produits n'ont pas d'ID valide");
      const response = await fetch("https://api.sgpme.biz/api/depot/achats/", { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ fournisseur: selectedFournisseur.id, succursale: selectedSuccursale.id, date_achat: dateAchat, notes: notes.trim() || null, lignes: lignesAvecProduits }) });
      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = "Erreur lors de l'enregistrement de l'achat";
        if (errorData.succursale) errorMessage = errorData.succursale[0];
        else if (errorData.lignes) errorMessage = errorData.lignes[0]?.produit?.[0] || errorData.lignes[0]?.quantite_par_conditionnement?.[0] || JSON.stringify(errorData.lignes);
        else if (errorData.detail) errorMessage = errorData.detail;
        throw new Error(errorMessage);
      }
      const achatCree = await response.json();
      for (const ligne of lignesAvecProduits) {
        try { await produitService.ajusterStock(ligne.produit, ligne.quantite * ligne.quantite_par_conditionnement, 'entree', `Achat N°${achatCree.numero || achatCree.id} - ${selectedSuccursale.nom}`); }
        catch { toast.warning(`Le stock n'a pas pu être mis à jour pour un produit`); }
      }
      toast.success("Achat enregistré avec succès !");
      setOpenDialog(false);
      await Promise.all([fetchAchats(page), fetchProduits()]);
      setSelectedFournisseur(null);
      setNotes("");
      setLignes([{ produit: null, produitNom: "", conditionnement: "", quantite: "", quantite_par_conditionnement: "", prix_achat_unitaire: "", prix_vente_unitaire: "" }]);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (user.succursale_principale_id && succursales.length > 0) {
        const sp = succursales.find(s => s.id === user.succursale_principale_id);
        if (sp) setSelectedSuccursale(sp);
      } else if (succursales.length === 1) { setSelectedSuccursale(succursales[0]); }
    } catch (error) { toast.error(error.message || "Une erreur est survenue"); }
  };

  // ── Carte mobile pour chaque achat ──
  const AchatCard = ({ achat }) => (
    <Card sx={{ mb: 1.5, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Chip label={achat.numero || achat.id} size="small" color="primary" variant="outlined" />
              <Typography variant="caption" color="textSecondary">{formatDate(achat.date_achat)}</Typography>
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {achat.fournisseur_detail?.nom || achat.fournisseur_nom || 'N/A'}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
              <Store fontSize="small" sx={{ color: 'action.active', fontSize: 14 }} />
              <Typography variant="caption" color="textSecondary">
                {achat.succursale_nom || achat.succursale?.nom || "N/A"}
              </Typography>
            </Box>
          </Box>
          <Box textAlign="right">
            <Typography variant="subtitle1" fontWeight={700} color="primary.main">
              {formatMontant(achat.montant_total)} HTG
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {achat.lignes?.length || 0} produit(s)
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
          <Button size="small" startIcon={<Visibility />} onClick={() => { setSelectedAchat(achat); setDetailDialogOpen(true); }}>
            Détails
          </Button>
          <Button size="small" startIcon={<PictureAsPdf />} onClick={() => downloadAchatPDF(achat)} disabled={loading}>
            PDF
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box p={{ xs: 1.5, sm: 2, md: 3 }}>

      {/* ── En-tête ── */}
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
            Achats Fournisseurs
          </Typography>
          {!isMobile && (
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<FilterList />} onClick={() => setShowFilters(!showFilters)} color={showFilters ? "primary" : "inherit"} size="small">
                Filtres
              </Button>
              <Button variant="contained" color="secondary" startIcon={<PictureAsPdf />} onClick={downloadRapportPDF} disabled={loading} size="small">
                Rapport PDF
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => setOpenDialog(true)} size="small">
                Nouvel Achat
              </Button>
            </Box>
          )}
        </Box>

        {/* Barre de recherche + actions mobile */}
        <Box display="flex" gap={1} mt={1.5} alignItems="center">
          <TextField
            placeholder="Rechercher..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active', fontSize: 18 }} /> }}
          />
          {isMobile && (
            <>
              <IconButton onClick={() => setShowFilters(!showFilters)} color={showFilters ? "primary" : "default"} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <FilterAlt fontSize="small" />
              </IconButton>
              <IconButton onClick={() => setOpenDialog(true)} color="primary" size="small" sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                <Add fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>

        {/* Boutons rapport mobile */}
        {isMobile && (
          <Button fullWidth variant="outlined" color="secondary" startIcon={<PictureAsPdf />} onClick={downloadRapportPDF} disabled={loading} size="small" sx={{ mt: 1 }}>
            Rapport PDF
          </Button>
        )}
      </Box>

      {/* ── Filtres ── */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FilterAlt fontSize="small" /> Options de filtrage
          </Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={6} md={2}>
              <TextField fullWidth label="Date début" type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth label="Date fin" type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Type de filtre</InputLabel>
                <Select value={activeFilterType} onChange={(e) => setActiveFilterType(e.target.value)} label="Type de filtre">
                  <MenuItem value="all">Tous</MenuItem>
                  <MenuItem value="fournisseur">Fournisseur</MenuItem>
                  <MenuItem value="succursale">Succursale</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete options={fournisseurs} getOptionLabel={(o) => o.nom || ''} value={fournisseurFilter} onChange={(e, v) => setFournisseurFilter(v)} size="small" disabled={activeFilterType === 'succursale'} renderInput={(params) => <TextField {...params} label="Fournisseur" />} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <Autocomplete options={succursales} getOptionLabel={(o) => o.nom || ''} value={succursaleFilter} onChange={(e, v) => setSuccursaleFilter(v)} size="small" disabled={activeFilterType === 'fournisseur'} renderInput={(params) => <TextField {...params} label="Succursale" />} />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button fullWidth variant="outlined" color="error" onClick={resetFilters} startIcon={<Close />} size="small">
                Reset
              </Button>
            </Grid>
          </Grid>
          {(dateDebut || dateFin || fournisseurFilter || succursaleFilter) && (
            <Box mt={1.5} p={1} bgcolor="action.hover" borderRadius={1}>
              <Typography variant="caption" color="textSecondary">
                <strong>Filtres actifs:</strong>
                {dateDebut && ` Du ${formatDate(dateDebut)}`}
                {dateFin && ` au ${formatDate(dateFin)}`}
                {fournisseurFilter && ` | ${fournisseurFilter.nom}`}
                {succursaleFilter && ` | ${succursaleFilter.nom}`}
              </Typography>
            </Box>
          )}
        </Paper>
      </Collapse>

      {/* ── Statistiques ── */}
      <Grid container spacing={{ xs: 1, md: 2 }} mb={2}>
        {[
          { label: "Achats", value: totalAchats },
          { label: "Fournisseurs", value: fournisseurs.length },
          { label: "Produits", value: produits.length },
          { label: "Succursales", value: succursales.length },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Typography color="textSecondary" variant="caption">{stat.label}</Typography>
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>{stat.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Liste : cards sur mobile, table sur desktop ── */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Typography textAlign="center" py={4} color="textSecondary">Chargement...</Typography>
          ) : achats.length === 0 ? (
            <Typography textAlign="center" py={4} color="textSecondary">Aucun achat trouvé</Typography>
          ) : (
            achats.map((achat) => <AchatCard key={achat.id} achat={achat} />)
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size={isTablet ? "small" : "medium"}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.light' }}>
                <TableCell>Numéro</TableCell>
                <TableCell>Fournisseur</TableCell>
                {!isTablet && <TableCell>Succursale</TableCell>}
                <TableCell>Date</TableCell>
                {!isTablet && <TableCell>Produits</TableCell>}
                <TableCell>Total</TableCell>
                {!isTablet && <TableCell>Utilisateur</TableCell>}
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center">Chargement...</TableCell></TableRow>
              ) : achats.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">Aucun achat trouvé</TableCell></TableRow>
              ) : achats.map((achat) => (
                <TableRow key={achat.id} hover>
                  <TableCell><Chip label={achat.numero || achat.id} size="small" color="primary" variant="outlined" /></TableCell>
                  <TableCell>{achat.fournisseur_detail?.nom || achat.fournisseur_nom || 'N/A'}</TableCell>
                  {!isTablet && (
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Store fontSize="small" sx={{ color: 'action.active' }} />
                        {achat.succursale_nom || achat.succursale?.nom || "N/A"}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell>{formatDate(achat.date_achat)}</TableCell>
                  {!isTablet && <TableCell>{achat.lignes?.length || 0} produit(s)</TableCell>}
                  <TableCell><Typography fontWeight={600}>{formatMontant(achat.montant_total)} HTG</Typography></TableCell>
                  {!isTablet && <TableCell>{achat.utilisateur_detail?.username || achat.utilisateur_username || 'N/A'}</TableCell>}
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, achat)} disabled={loading}>
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" size={isMobile ? "small" : "medium"} showFirstButton showLastButton />
        </Box>
      )}

      {/* ── Menu actions ── */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItemMui onClick={handleViewDetails}><Visibility fontSize="small" sx={{ mr: 1.5 }} />Voir les détails</MenuItemMui>
        <MenuItemMui onClick={() => { downloadAchatPDF(selectedAchat); handleMenuClose(); }}><PictureAsPdf fontSize="small" sx={{ mr: 1.5 }} />Télécharger PDF</MenuItemMui>
      </Menu>

      {/* ── Dialog détail ── */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Achat N°{selectedAchat?.numero || selectedAchat?.id}</Typography>
            <Box display="flex" gap={1}>
              <Button variant="outlined" startIcon={<PictureAsPdf />} onClick={() => { downloadAchatPDF(selectedAchat); setDetailDialogOpen(false); }} size="small" disabled={loading}>PDF</Button>
              <IconButton size="small" onClick={() => setDetailDialogOpen(false)}><Close /></IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAchat && (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Fournisseur</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAchat.fournisseur_detail?.nom || 'N/A'}</Typography>
                  {selectedAchat.fournisseur_detail?.telephone && <Typography variant="body2">Tél: {selectedAchat.fournisseur_detail.telephone}</Typography>}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="textSecondary">Succursale</Typography>
                  <Typography variant="body1" fontWeight={600}>{selectedAchat.succursale_nom || selectedAchat.succursale?.nom || "N/A"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Date</Typography>
                  <Typography variant="body1">{formatDate(selectedAchat.date_achat)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Utilisateur</Typography>
                  <Typography variant="body1">{selectedAchat.utilisateur_detail?.username || 'N/A'}</Typography>
                </Grid>
                {selectedAchat.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Notes</Typography>
                    <Typography variant="body1">{selectedAchat.notes}</Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Produits achetés</Typography>
              {isMobile ? (
                // Détail produits en cards sur mobile
                selectedAchat.lignes?.map((ligne, i) => (
                  <Card key={i} sx={{ mb: 1, bgcolor: 'grey.50' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="subtitle2" fontWeight={600}>{ligne.produit_detail?.nom || 'N/A'}</Typography>
                      <Grid container spacing={1} mt={0.5}>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">Conditionnement</Typography><Typography variant="body2">{ligne.conditionnement}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">Quantité</Typography><Typography variant="body2">{parseFloat(ligne.quantite).toFixed(2)} × {ligne.quantite_par_conditionnement}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">Prix achat</Typography><Typography variant="body2">{formatMontant(ligne.prix_achat_unitaire)} HTG</Typography></Grid>
                        <Grid item xs={6}><Typography variant="caption" color="textSecondary">Total</Typography><Typography variant="body2" fontWeight={600} color="primary">{formatMontant(ligne.total || (parseFloat(ligne.quantite) * ligne.quantite_par_conditionnement * parseFloat(ligne.prix_achat_unitaire)))} HTG</Typography></Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Produit</TableCell>
                        <TableCell align="center">Cond.</TableCell>
                        <TableCell align="center">Qté</TableCell>
                        <TableCell align="center">Qté/U</TableCell>
                        <TableCell align="right">P. Achat</TableCell>
                        <TableCell align="right">P. Vente</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedAchat.lignes?.map((ligne, i) => (
                        <TableRow key={i}>
                          <TableCell>{ligne.produit_detail?.nom || 'N/A'}</TableCell>
                          <TableCell align="center">{ligne.conditionnement}</TableCell>
                          <TableCell align="center">{parseFloat(ligne.quantite).toFixed(2)}</TableCell>
                          <TableCell align="center">{ligne.quantite_par_conditionnement}</TableCell>
                          <TableCell align="right">{formatMontant(ligne.prix_achat_unitaire)} HTG</TableCell>
                          <TableCell align="right">{formatMontant(ligne.prix_vente_unitaire)} HTG</TableCell>
                          <TableCell align="right">{formatMontant(ligne.total || (parseFloat(ligne.quantite) * ligne.quantite_par_conditionnement * parseFloat(ligne.prix_achat_unitaire)))} HTG</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={6} align="right"><strong>Total :</strong></TableCell>
                        <TableCell align="right"><Typography variant="subtitle1" color="primary" fontWeight={700}>{formatMontant(selectedAchat.montant_total)} HTG</Typography></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {isMobile && (
                <Box mt={2} p={1.5} bgcolor="primary.50" borderRadius={1} textAlign="right">
                  <Typography variant="subtitle1" fontWeight={700} color="primary">Total : {formatMontant(selectedAchat.montant_total)} HTG</Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog nouvel achat ── */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <ShoppingCart />
              <Typography variant="h6">Nouvel Achat</Typography>
            </Box>
            {isMobile && <IconButton size="small" onClick={() => setOpenDialog(false)}><Close /></IconButton>}
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete options={fournisseurs} getOptionLabel={(o) => o.nom || ''} value={selectedFournisseur} onChange={(e, v) => setSelectedFournisseur(v)} renderInput={(params) => <TextField {...params} label="Fournisseur *" required fullWidth />} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete options={succursales} getOptionLabel={(o) => o.nom || ''} value={selectedSuccursale} onChange={(e, v) => setSelectedSuccursale(v)} loading={loading} renderInput={(params) => <TextField {...params} label="Succursale de destination *" required fullWidth />} />
                <Typography variant="caption" color="textSecondary">Le stock sera ajouté à cette succursale</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Date d'achat *" type="date" value={dateAchat} onChange={(e) => setDateAchat(e.target.value)} InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Notes (optionnel)" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Informations supplémentaires..." />
              </Grid>

              {/* Lignes produits */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Produits achetés</Typography>
                {lignes.map((ligne, index) => (
                  <Paper key={index} sx={{ p: { xs: 1.5, md: 2 }, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                    {/* Ligne 1 : Produit + Conditionnement */}
                    <Grid container spacing={1.5} mb={1.5}>
                      <Grid item xs={12} sm={8}>
                        <Autocomplete freeSolo options={produits} getOptionLabel={(o) => typeof o === 'string' ? o : o.nom || ''} inputValue={ligne.produitNom}
                          onInputChange={(e, v) => { handleLigneChange(index, 'produitNom', v); }}
                          renderInput={(params) => <TextField {...params} label="Produit *" required placeholder="Nom du produit" fullWidth size="small" />}
                          renderOption={(props, option) => <li {...props}><Typography variant="body2">{option.nom}</Typography></li>}
                        />
                        {ligne.produit === null && ligne.produitNom && (
                          <Button size="small" variant="outlined" onClick={() => handleCreateQuickProduit(index)} sx={{ mt: 0.5 }} fullWidth>
                            Créer "{ligne.produitNom}"
                          </Button>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Conditionnement *</InputLabel>
                          <Select value={ligne.conditionnement || ""} onChange={(e) => handleLigneChange(index, 'conditionnement', e.target.value)} label="Conditionnement *" required>
                            <MenuItem value="">Sélectionner...</MenuItem>
                            {conditionnementOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Ligne 2 : Quantités + Prix */}
                    <Grid container spacing={1.5} alignItems="center">
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth label="Qté" type="number" value={ligne.quantite} onChange={(e) => handleLigneChange(index, 'quantite', e.target.value)} InputProps={{ inputProps: { min: 0, step: 0.01 } }} size="small" required />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <TextField fullWidth label="Qté/Unité" type="number" value={ligne.quantite_par_conditionnement} onChange={(e) => handleLigneChange(index, 'quantite_par_conditionnement', e.target.value)} InputProps={{ inputProps: { min: 0, step: 0.01 } }} size="small" required />
                      </Grid>
                      <Grid item xs={6} sm={2.5}>
                        <TextField fullWidth label="Prix achat" type="number" value={ligne.prix_achat_unitaire} onChange={(e) => handleLigneChange(index, 'prix_achat_unitaire', e.target.value)} InputProps={{ inputProps: { min: 0, step: 0.01 } }} size="small" required />
                      </Grid>
                      <Grid item xs={6} sm={2.5}>
                        <TextField fullWidth label="Prix vente" type="number" value={ligne.prix_vente_unitaire} onChange={(e) => handleLigneChange(index, 'prix_vente_unitaire', e.target.value)} InputProps={{ inputProps: { min: 0, step: 0.01 } }} size="small" required />
                      </Grid>
                      <Grid item xs={12} sm={1} display="flex" justifyContent={{ xs: 'flex-end', sm: 'center' }}>
                        <IconButton onClick={() => handleRemoveLigne(index)} disabled={lignes.length === 1} color="error" size="small">
                          <Delete />
                        </IconButton>
                      </Grid>
                    </Grid>

                    {/* Total ligne */}
                    {ligne.produitNom && ligne.quantite && ligne.quantite_par_conditionnement && ligne.prix_achat_unitaire && (
                      <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="textSecondary">
                          Total: <strong>{calculateLigneTotal(ligne)} HTG</strong>
                        </Typography>
                        {ligne.produit ? (
                          <Chip label="Produit existant" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label="Nouveau produit" size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    )}
                  </Paper>
                ))}

                <Button startIcon={<Add />} onClick={handleAddLigne} variant="outlined" fullWidth>
                  Ajouter une ligne
                </Button>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ p: { xs: 2, md: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
            {/* Total sur mobile */}
            {isMobile && totalAchat > 0 && (
              <Box width="100%" textAlign="right" mb={1}>
                <Typography variant="subtitle1" fontWeight={700} color="primary">
                  Total: {totalAchat.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} HTG
                </Typography>
              </Box>
            )}
            <Button onClick={() => setOpenDialog(false)} fullWidth={isMobile}>Annuler</Button>
            <Button type="submit" variant="contained" color="primary" fullWidth={isMobile}
              disabled={!selectedFournisseur || !selectedSuccursale || lignes.some(l => !l.produitNom.trim() || !l.conditionnement || !l.quantite || !l.quantite_par_conditionnement || !l.prix_achat_unitaire || !l.prix_vente_unitaire)}>
              Enregistrer l'achat
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default AchatsFournisseurs;
